import { useEffect, useMemo, useState } from 'react'
import { Info, Users, User } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDirectory } from '@/contexts/directory.context'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  directoryApiClient,
  type DirectoryAssigneePreview,
  type DirectoryAssigneeRef,
  type RoleWithMembers,
  type Staff,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import {
  describeAssigneePreview,
  formatResolvedAt,
  type AssigneeHint,
} from '@/lib/push-assignee-hint'

type PickerItem = { kind: 'staff' | 'role'; id: string }

// Roles first (they're the reason to use the directory at all), then staff.
// Each staff option says whether that person has a code for this integration:
// picking someone unmapped silently pushes no assignee, so it has to be
// visible while choosing, not only afterwards in the hint.
export function buildAssigneeItems({
  roles,
  staff,
  integrationType,
  integrationName,
}: {
  roles: RoleWithMembers[]
  staff: Staff[]
  integrationType: string
  integrationName: string
}): SearchDropdownItem<PickerItem>[] {
  const roleItems = roles.map(role => ({
    id: `role:${role.id}`,
    label: role.name,
    sublabel: `Role · ${role.members.length} member${
      role.members.length === 1 ? '' : 's'
    }`,
    icon: <Users className="h-4 w-4" />,
    data: { kind: 'role' as const, id: role.id },
  }))
  const staffItems = staff.map(member => {
    const mapped = member.integrationAssignees?.find(
      assignee => assignee.integrationType === integrationType,
    )
    return {
      id: `staff:${member.id}`,
      label: member.name,
      sublabel: mapped
        ? `${integrationName} ID: ${mapped.value}`
        : `No ${integrationName} ID mapped`,
      icon: <User className="h-4 w-4" />,
      data: { kind: 'staff' as const, id: member.id },
    }
  })
  return [...roleItems, ...staffItems]
}

interface PushAssigneeFieldProps {
  label: string
  description?: string
  // Hint for the manual box, from the field's own config — a Yardi labor
  // employee is a name or a code, not the generic numeric id (SUP-2590).
  placeholder?: string
  // The literal value typed by hand. Wins over the reference at dispatch.
  value: string
  // The staff/role picked from the directory, resolved when the push fires.
  assigneeRef: DirectoryAssigneeRef | undefined
  integrationId: string
  integrationType: string
  integrationName: string
  actionGroupId: string
  onChange: (value: string) => void
  onRefChange: (ref: DirectoryAssigneeRef | undefined) => void
}

// Assignee field for a push action. Two ways to fill it, deliberately kept
// separate rather than merged into one free-text box: picking a staff member
// or role stores a *reference* that resolves at dispatch (so an on-call role
// stays live), while typing stores a literal value that is pushed as-is. The
// hint underneath states which one will actually be used, resolved server-side
// so it can't drift from dispatch.
export function PushAssigneeField({
  label,
  description,
  placeholder,
  value,
  assigneeRef,
  integrationId,
  integrationType,
  integrationName,
  actionGroupId,
  onChange,
  onRefChange,
}: PushAssigneeFieldProps) {
  const { roles, staff, loading: directoryLoading } = useDirectory()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  // A saved literal value means this action was configured by hand — open in
  // the mode the admin left it in.
  const [manualMode, setManualMode] = useState(!!value)
  const [preview, setPreview] = useState<DirectoryAssigneePreview | null>(null)
  const [resolvedAt, setResolvedAt] = useState<Date | null>(null)
  const [failed, setFailed] = useState(false)

  const items = useMemo(
    () =>
      buildAssigneeItems({ roles, staff, integrationType, integrationName }),
    [roles, staff, integrationType, integrationName],
  )

  const selectedItem = assigneeRef
    ? (items.find(
        item => item.id === `${assigneeRef.kind}:${assigneeRef.id}`,
      ) ?? {
        id: `${assigneeRef.kind}:${assigneeRef.id}`,
        // While the directory is still loading, a miss just means the list
        // isn't populated yet — only call it "Deleted" once loading has
        // finished and the ref still doesn't resolve to anything.
        label: directoryLoading ? 'Loading…' : `Deleted ${assigneeRef.kind}`,
        data: { kind: assigneeRef.kind, id: assigneeRef.id },
      })
    : null

  useEffect(() => {
    const trimmedValue = value.trim()
    // A literal override always wins at dispatch and is pushed exactly as
    // typed — the server-side preview for it is just `{ source: 'manual',
    // value }` echoed back. Building that locally means typing in the manual
    // field no longer fires a network request per keystroke.
    if (trimmedValue) {
      setPreview({
        staff: null,
        coveringFor: null,
        roleName: null,
        schedulingMode: null,
        hasRotationFallback: false,
        value: trimmedValue,
        source: 'manual',
        candidates: [],
      })
      setResolvedAt(new Date())
      setFailed(false)
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        const params = {
          integrationId,
          ref: assigneeRef,
          manualValue: undefined,
        }
        const result =
          isSuperAdmin && currentCompany?.id
            ? await directoryAdminApiClient.previewAssignee(
                currentCompany.id,
                actionGroupId,
                params,
              )
            : await directoryApiClient.previewAssignee(actionGroupId, params)
        if (cancelled) return
        setPreview(result)
        setResolvedAt(new Date())
        setFailed(false)
      } catch {
        if (cancelled) return
        // The hint is advisory — a failed preview must never block editing.
        setFailed(true)
        setPreview(null)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [
    integrationId,
    actionGroupId,
    assigneeRef?.kind,
    assigneeRef?.id,
    value,
    isSuperAdmin,
    currentCompany?.id,
  ])

  const hint: AssigneeHint | null = preview
    ? describeAssigneePreview(preview, integrationName)
    : null

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        <button
          type="button"
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => setManualMode(prev => !prev)}
        >
          {manualMode ? 'Pick from directory' : 'Enter manually'}
        </button>
      </div>

      {manualMode ? (
        <Input
          placeholder={placeholder ?? 'e.g. 12345'}
          value={value}
          onChange={event => onChange(event.target.value)}
        />
      ) : (
        <SearchDropdown<PickerItem>
          items={items}
          selectedItems={selectedItem}
          onSelect={item => {
            const selected = item as SearchDropdownItem<PickerItem> | null
            // Clearing the manual override has to land in the same update as
            // the ref, not a follow-up onChange('') call — two calls in a row
            // would each be computed off the same pre-pick parameters, so the
            // second silently drops whatever the first just set.
            onRefChange(
              selected?.data
                ? { kind: selected.data.kind, id: selected.data.id }
                : undefined,
            )
          }}
          buttonLabel={selectedItem?.label ?? 'Select staff or role…'}
          placeholder="Select staff or role…"
          searchPlaceholder="Search staff and roles…"
          emptyMessage="No staff or roles found"
          buttonClassName="w-full [&_.truncate]:ml-0"
        />
      )}

      {hint && (
        <div
          className={`flex items-start gap-1 text-xs ${
            hint.tone === 'warning' ? 'text-amber-600' : 'text-muted-foreground'
          }`}
        >
          <span className="min-w-0 flex-1">
            {hint.tone === 'warning' ? '⚠ ' : ''}
            {hint.summary}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 cursor-help pt-0.5">
                  <Info className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs space-y-1">
                <p>{hint.summary}</p>
                {hint.detail && (
                  <p className="text-muted-foreground">{hint.detail}</p>
                )}
                {resolvedAt && (
                  <p className="text-muted-foreground">
                    {formatResolvedAt(resolvedAt, currentCompany?.timezoneName)}{' '}
                    — resolved again each time this action is pushed.
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {failed && (
        <p className="text-xs text-muted-foreground">
          Couldn’t preview who this resolves to right now.
        </p>
      )}

      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}
